import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";

import axios from "axios";
import axiosRetry from "axios-retry";

import toast from "react-hot-toast";
import {
  getAxiosApiClient,
  addAxiosRetry,
  getAxiosConfig,
} from "utils/apiRequestUtils";
// import checkValidJsonParse from "utils/checkValidJsonParse";
// import { logOut } from "redux/loginSlice";

import { backendDomain } from "utils/envConfig";
import useToastMessage from "hooks/useApiRequest/useToastMessage";

function useApiRequest(
  method,
  url,
  {
    requestHeaders = null,
    requestBody = null,
    useRetry = false,
    retryOptions = null,
  } = {}
) {
  /* 
    React hook to call APIs and handle their response and error messages.

    Params:
      - method
      - url
      - requestHeaders (optional)
      - requestBody (optional)
      - useRetry (optional)
      - retryOptions (optional)
  */
  // TODO: remove console log parameter values for debugging
  console.log(url, "useApiRequest() input:", {
    method: method,
    url: url,
    requestHeaders: requestHeaders,
    requestBody: requestBody,
    useRetry: useRetry,
    retryOptions: retryOptions,
  });

  // init states
  const [apiResponseData, setApiResponseData] = useState(null);
  const [apiLoading, setApiLoading] = useState(null);
  const [apiError, setApiError] = useState(null);

  useToastMessage(apiError?.data?.detail); // hook to show toast message when state is updated

  console.log(
    "useApiRequest() states values:",
    apiResponseData,
    apiLoading,
    apiError
  );

  // init useNavigate and useDispatch hooks
  // const navigate = useNavigate();
  // const dispatch = useDispatch();

  // const requestHeadersMemoized = useMemo(() => requestHeaders, [requestHeaders])

  const makeAxiosCall = useCallback(async () => {
    /*
     * Function to make the axios request to the specified API.
     * This function will also be memoized using useCallback() to prevent unnecessary re-renders,
     * which happens when there's change in the state of the component page that it will be used in,
     * i.e. unnecessary re-render due to referential inequality.
     */

    // Init axios client instance with baseURL from .env file
    const axiosApiClient = getAxiosApiClient(backendDomain, retryOptions);

    if (useRetry) {
      // if useRetry is truthy, setup axios-retry with custom retryOptions (or default values, if retryOptions is null)
      addAxiosRetry(axiosApiClient, retryOptions);
    }

    // init config values for axios, axios config docs: https://axios-http.com/docs/req_config
    const axiosConfig = getAxiosConfig(
      method,
      url,
      requestHeaders,
      requestBody
    );

    console.log(url, "axiosConfig:", axiosConfig);

    try {
      setApiResponseData(null)
      setApiLoading(true);
      setApiError(null);

      // call api using axios client + axios config
      const response = await axiosApiClient.request(axiosConfig);

      setApiResponseData(response.data);
      setApiLoading(false);

      console.log(url, "successful response:", response);
    } catch (error) {
      // on API call error, axios returns AxiosError object by default
      console.error(url, "error during API call:", error);

      if (!axios.isAxiosError(error)) {
        // if error is not related to axios API call
        // TODO: add some logic to output here?
        toast.error("Error: Something went wrong.", {
          id: "not_axios_error",
        });
        return;
      }
      setApiError(error.response);
      setApiLoading(false);

      // throw error; // Optionally re-throw to handle the error outside of this function
      // throw Error("custom error");
    } finally {
      setApiLoading(false);
      console.log(url, `Request completed`); // Log completion of the request
    }
  }, [method, url, requestHeaders, requestBody, useRetry, retryOptions]);

  return { makeAxiosCall, apiResponseData, apiLoading, apiError };
}

export default useApiRequest;
